<template>
    <div ref="ppe" class="ppe">
        <v-text-field
            id="ppe-input"
            v-model="inputValue"
            label="Amount to Finance"
            @blur="onBlur"
            @focus="focusValue = inputValue"
            @keypress="onkeypress">
            <template slot="append">
                <button type="button" class="ppe-btn plus" :disabled="addDisabled" @click="increment">+</button>
            </template>
            <template slot="prepend">
                <button type="button" class="ppe-btn minus" :disabled="minusDisabled" @click="decrement">-</button>
            </template>
        </v-text-field>
    </div>
</template>

<script>
    import { formatCurrency } from "@/util/currency"

    export default {
        name: "PaymentPlanEstimator",
        props: {
            value: {
                type: Number,
            },
            min: {
                type: Number,
                default: 0,
            },
            max: {
                type: Number,
            },
            step: {
                type: Number,
                default: 500,
            },

            hideSteps: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                money: this.value,
                focusValue: null,
            }
        },
        computed: {
            inputValue: {
                get() {
                    return formatCurrency(this.value, "dollars", false)
                },
                set(value) {
                    const parsed = parseFloat(value.replace(/[^0-9.]/g, ""))
                    if (!parsed) {
                        this.update(0)
                    } else {
                        this.update(parseFloat(value.replace(/[^0-9.]/g, "")))
                    }
                },
            },

            addDisabled() {
                return this.value >= this.max
            },

            minusDisabled() {
                return this.value <= this.min
            },
        },

        methods: {
            onkeypress(evt) {
                if (evt.key === "-" || evt.key === ".") {
                    evt.stopPropagation()
                    evt.preventDefault()
                }
            },

            onBlur() {
                if (this.focusValue !== this.inputValue) {
                    const value = this.scrubValue(this.value)
                    // we use stepper emit to initiate payment recalc
                    this.$emit("step", value)
                    this.$emit("input", value)
                }
            },

            blur() {
                this.$refs.ppe.querySelector("input")?.blur()
            },

            decrement() {
                this.stepValue(0 - this.step)
            },

            increment() {
                this.stepValue(this.step)
            },

            stepValue(increment) {
                const value = this.scrubValue(this.value + increment)
                this.$emit("step", value)
                this.$emit("input", value)
            },

            update(evt) {
                let value = evt

                if (typeof evt === "object") {
                    value = evt.target.value
                }

                if (value != null && value !== this.value) {
                    value = this.scrubValue(value)
                    this.$emit("type", value)
                    this.$emit("input", value)
                }
            },

            scrubValue(value) {
                if (this.max != null) {
                    value = Math.min(this.max, value)
                }

                return value
            },
        },
    }
</script>

<style lang="scss">
    .ppe {
        input {
            font-weight: bold;
            text-align: center;
        }

        .v-text-field {
            padding: 0;
            margin: 0;
        }
        .v-input__prepend-outer {
            margin: 0;
        }
        .v-input__append-inner {
            margin: 0;
        }

        .v-text-field__details {
            display: none;
        }
        .v-label {
            transform: translateY(-25px) translateX(8px) scale(0.75);
            background-color: #fff;
            padding-left: 5px;
            padding-right: 5px;
        }

        .v-input__slot {
            &::before {
                border: 0 !important;
            }
            margin-bottom: 0;
        }
    }
</style>

<style lang="scss" scoped>
    $borderStyleGray: 1px solid $grey-darken-1;
    $borderStyleSecondary: 1px solid $secondary-color;
    .ppe {
        background-color: #fff;
        user-select: none;
        width: 100%;
        margin: auto;
        border-radius: 6px;
        display: flex;
        border: $borderStyleGray;
        border-left: 0;
        border-right: 0;
        div {
            display: flex;
            align-items: flex-end;
        }

        .ppe-btn {
            background-color: $secondary-color;
            font-size: 2.75rem;
            font-weight: 300;
            color: #fff;
            height: 56px;
            width: 56px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;

            &.minus {
                border-radius: 4px 0 0 4px;
                border: $borderStyleSecondary;

                &:hover {
                    background-color: #ddd;
                    color: #002855;
                    border: $borderStyleGray;
                }

                &[disabled] {
                    background: lighten($color: #e0e0e0, $amount: 5%);
                    color: rgb(175, 175, 175);
                    border-radius: 4px 0 0 4px;
                    border: $borderStyleGray;
                }
            }

            &.plus {
                border-radius: 0 4px 4px 0;
                border: $borderStyleSecondary;

                &:hover {
                    background-color: #ddd;
                    color: #002855;
                    border: $borderStyleGray;
                }

                &[disabled] {
                    background: lighten($color: #e0e0e0, $amount: 5%);
                    color: rgb(175, 175, 175);
                    border-radius: 0 4px 4px 0;
                    border: $borderStyleGray;
                }
            }
        }
    }
</style>
